<script>
import RepCounter from '@components/counters/rep-counter.vue';
import EditableText from '@components/editables/editable-text.vue';
import SetDetailsCircuit from '@components/circuit/set-details-circuit.vue';
import { circuitModelMixin } from '@mixins/models/circuit-model-mixin';
import { mixin as clickaway } from 'vue-clickaway';
import {
  adminPanelComputed,
  circuitComputed,
  circuitMethods,
  exerciseComputed,
  exerciseMethods,
  fitnessTypeComputed,
  workoutComputed,
  workoutMethods,
} from '@state/helpers';

export default {
  components: {
    EditableText,
    RepCounter,
    SetDetailsCircuit,
  },
  props: {
    circuit: {
      type: Object,
      required: true,
      default: () => {},
    },
    circuitIndex: {
      type: Number,
      required: true,
      default: null,
    },
    selectedCircuitIndex: {
      type: Number,
      required: false,
      default: null,
    },
    inPanel: {
      type: Boolean,
      required: false,
    },
    overActionFooter: {
      type: Boolean,
      required: false,
    },
    allowEditing: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  mixins: [circuitModelMixin, clickaway],
  data() {
    return {};
  },
  computed: {
    ...adminPanelComputed,
    ...exerciseComputed,
    ...circuitComputed,
    ...workoutComputed,
    ...fitnessTypeComputed,
    hasExercises() {
      return (
        this.circuit && this.circuit.exercises && this.circuit.exercises.length
      );
    },
    isSelected() {
      return this.circuitIndex === this.selectedCircuitIndex;
    },
    exercisesLength() {
      return this.circuit.exercises.length;
    },
  },
  methods: {
    ...exerciseMethods,
    ...circuitMethods,
    ...workoutMethods,
    updateTitle(value) {
      this.setAddWorkoutCircuitLabel({
        circuitIndex: this.circuitIndex,
        label: value,
      });
    },
    updateCircuitDetails(type, value) {
      this.setAddWorkoutCircuitDetails({
        circuitIndex: this.circuitIndex,
        text: value,
        type,
      });
    },
    onAdjustReps(circuitRep) {
      this.setUpdateWorkoutCircuitReps({
        circuitIndex: this.circuitIndex,
        count: circuitRep,
      });
    },
    showExerciseList() {
      this.$emit('show-exercise-list', this.circuitIndex);
    },
    selectExercise() {
      this.setSelectedExercise({});
    },
    removeBlankCircuit() {
      this.$emit('remove-blank-circuit', this.circuitIndex);
    },
    selectCircuit(event) {
      if (this.allowEditing) {
        const classList = event.target.classList;
        if (
          classList.contains('card-circuit-header') ||
          classList.contains('action-edit')
        ) {
          if (this.isSelected) {
            this.$emit('on-select-circuit', null);
            this.setSelectedCircuit(null);
          } else {
            this.setSelectedCircuit(this.circuit);
            this.$emit('on-select-circuit', this.circuitIndex);
          }
        }
      }
    },
    clickedOutside() {
      if (
        typeof this.selectedCircuitIndex === 'number' &&
        typeof this.circuitIndex === 'number' &&
        this.selectedCircuitIndex === this.circuitIndex &&
        !this.overActionFooter &&
        !this.overCircuitDetails
      ) {
        this.$emit('on-select-circuit', null);
        this.setSelectedCircuit(null);
      }
    },
  },
  watch: {
    selectedCircuitIndex(n, o) {
      if (this.circuitIndex === this.selectedCircuitIndex) {
        const element = this.$refs.cardCircuit;
        const navbar = document.getElementsByClassName('navbar-wrapper')[0];
        const containerWrapper = document.getElementsByClassName(
          'container-wrapper'
        )[0];
        const paneLeft = document.getElementsByClassName('pane-left')[0];
        if (paneLeft && this.inPanel) {
          paneLeft.scroll({
            top: element.offsetTop,
            behavior: 'smooth',
          });
        }
        if (containerWrapper && !this.inPanel) {
          containerWrapper.scroll({
            top: element.offsetTop - navbar.clientHeight - 10,
            behavior: 'smooth',
          });
        }
      }
    },
    exercisesLength(newVal, oldVal) {
      this.setAddUniqueId();
    },
  },
};
</script>

<template>
  <div class="card-circuit-container" ref="cardCircuit">
    <div
      :class="[
        'card-circuit-header',
        { 'allow-editing': allowEditing },
        { 'details-visible': isSelected },
        { selected: isSelected },
        { inPanel: inPanel },
      ]"
      @click="selectCircuit($event)"
      v-on-clickaway="clickedOutside"
    >
      <h2>
        <EditableText
          v-if="allowEditing"
          :text="circuit.label"
          placeholderTxt="Circuit Name"
          mode="edit"
          :canEdit="true"
          @update-editable-value="updateTitle"
        />
        <span v-if="!allowEditing">{{ circuit.label }}</span>
        <span class="action-edit" v-if="!isSelected && allowEditing">
          Edit Circuit
        </span>
      </h2>
      <RepCounter
        :allowEditing="allowEditing"
        :count="circuit.circuitReps"
        @adjust-reps="onAdjustReps"
      />
    </div>
    <div
      class="circuit-details"
      v-show="isSelected"
      @mouseover="() => (overCircuitDetails = true)"
      @mouseleave="() => (overCircuitDetails = false)"
    >
      <div class="col">
        <BaseInputText
          class="input-intro"
          :light="true"
          placeholder="Circuit intro"
          @update="updateCircuitDetails('intro', $event)"
          :value="circuit.trainerFeedback.intro.text"
        />
        <div class="helper-text">
          Your client will see this preview text before the circuit.
        </div>
      </div>
      <div class="col">
        <BaseInputText
          class="input-intro"
          :light="true"
          :value="circuit.trainerFeedback.outro.text"
          placeholder="Circuit outro"
          @update="updateCircuitDetails('outro', $event)"
        />
        <div class="helper-text">
          Your client will see this when they complete the circuit.
        </div>
      </div>
      <div class="col">
        <h6 class="section-title">Fitness Type</h6>
        <v-select
          multiple
          id="circuitFitnessType"
          name="circuitFitnessType"
          placeholder="Choose a fitness type"
          :options="allFitnessTypes"
          :searchable="false"
          :clearable="false"
          :value="circuit.fitness_types"
          @input="updateCircuitDetails('fitnessType', $event)"
          :reduce="item => ({ _id: item._id, label: item.label })"
        />
      </div>
    </div>
    <transition-group
      class="card-circuit-content"
      tag="div"
      name="swap-cards"
      v-if="hasExercises"
    >
      <SetDetailsCircuit
        v-for="(exercise, i) in circuit.exercises"
        :key="exercise.uniqueKey"
        :exercise="exercise"
        :circuitIndex="circuitIndex"
        :exerciseIndex="i"
        :inPanel="inPanel"
        @show-exercise-list="showExerciseList"
        :allowEditing="allowEditing"
      />
    </transition-group>
    <div class="card-circuit-content empty" v-if="!hasExercises">
      <div class="add-new-exercise-wrapper">
        <p> No exercises yet. <br />Add your first exercise to the circuit. </p>
        <BaseButton
          type="button"
          class="btn-secondary btn-round"
          @click="showExerciseList"
        >
          <svg width="21" height="23" viewBox="0 0 21 23" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-curl'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M4.18 7.87c2.032 0 3.678-1.761 3.678-3.935C7.858 1.762 6.212 0 4.181 0S.503 1.762.503 3.935c0 2.174 1.647 3.936 3.678 3.936zm0-2.66c.678 0 1.226-.595 1.226-1.33 0-.735-.548-1.33-1.225-1.33s-1.226.595-1.226 1.33c0 .735.549 1.33 1.226 1.33zM.125 10.03a2.07 2.07 0 00.172 1.77l5.737 9.528a1.694 1.694 0 002.487.467l9.845-7.607A4.974 4.974 0 0020.29 9.98l-.032-.577a4.186 4.186 0 00-3.726-3.932 4.279 4.279 0 00-4.018 1.872L7.387 15 2.786 9.304a1.487 1.487 0 00-2.555.428l-.107.297z" fill="#000"/></svg>
          Add First Exercise
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.card-circuit-container {
  background-color: white;
  border-radius: $base-border-radius-lg;
  box-shadow: 0 0.2rem 0.4rem rgba($color-black-wo-rgb, 0.08);
  margin-bottom: 1.6rem;
  transition: transform 300ms;
  transition-timing-function: ease-out;

  .btn-primary {
    span {
      white-space: nowrap;
    }
  }

  &:hover {
    box-shadow: 0 0.4rem 0.8rem rgba($color-black-wo-rgb, 0.1);
    transform: scale(1.005);
  }

  .card-circuit-header {
    align-items: center;
    border-bottom: 6px solid $color-card-circuit-header-border;
    box-sizing: content-box;
    display: flex;
    justify-content: space-between;
    min-height: 9rem;
    padding: 1.5rem 2.5rem;

    &.inPanel {
      ::v-deep .rep-controls {
        margin: unset !important;
        margin-right: 3% !important;
      }
    }

    &:hover {
      &.allow-editing {
        background-color: $color-card-circuit-header-hover;
        border-top-left-radius: $base-border-radius-lg;
        border-top-right-radius: $base-border-radius-lg;
        cursor: pointer;
      }
    }

    &.details-visible {
      border-bottom: 0;
    }

    h2 {
      align-items: center;
      display: flex;
      margin-bottom: 0;
      position: relative;
      width: 60%;

      @media (max-width: 600px) {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      span {
        width: 100%;
      }
      ::v-deep .input-inline-edit {
        background: $color-off-white;
        border: 0;
        font-size: 2.4rem;
        font-weight: 900;
        letter-spacing: -0.466667px;
        line-height: 5rem;
        padding: 0 2rem;
      }
    }

    .action-edit {
      color: $color-dark-gray-one;
      font-size: 1.4rem;
      font-weight: normal;
      letter-spacing: -0.0833333px;
      line-height: 1.9rem;
      margin-left: 3rem;
      mix-blend-mode: normal;
      opacity: 0.5;
      text-decoration-line: underline;
      white-space: nowrap;
      width: fit-content;

      &:hover {
        cursor: pointer;
      }

      @media (max-width: 600px) {
        padding-top: 1rem;
      }
    }

    &.selected {
      background-color: $color-card-circuit-header-bg-selected;
      border-color: $color-action;
      border-top-left-radius: $base-border-radius-lg;
      border-top-right-radius: $base-border-radius-lg;
    }
  }

  .circuit-details {
    background-color: $color-off-white-two;
    display: flex;
    height: 9.7rem;
    padding: 1.6rem;

    ::v-deep .input-intro input {
      margin-bottom: 0.8rem;
    }

    .helper-text {
      color: $color-dark-gray-two;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.1px;
      line-height: 12px;
      mix-blend-mode: normal;
      opacity: 0.6;
      text-transform: uppercase;
    }
  }

  .card-circuit-content {
    min-height: 22.6rem;

    &.empty {
      align-items: center;
      display: flex;
      justify-content: center;
    }

    .add-new-exercise-wrapper {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      width: 25%;

      .icon-curl {
        path {
          fill: $color-action;
        }
      }
    }
    .action-remove {
      color: $color-red;
      font-size: 1.4rem;
      font-weight: 800;
      letter-spacing: 0.116667px;
      line-height: 1.9rem;
      margin-top: 2rem;

      &:hover {
        cursor: pointer;
      }

      .icon-trash {
        margin-right: 0.7rem;

        path {
          stroke: $color-red;
        }
      }
    }
  }

  .swap-cards-move {
    transition: transform 300ms;
  }
}
</style>
